import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import { ShieldCheck, Download, AlertTriangle } from 'lucide-react';
import './CertificateVerify.css';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const CertificateVerify = () => {
    const { id } = useParams();
    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [qrImg, setQrImg] = useState(null);

    useEffect(() => {
        const fetchCertificate = async () => {
            try {
                const res = await axios.get(`${API_URL}/certificates/verify/${id}`);
                setCertificate(res.data);
            } catch (err) {
                setError(err.response?.data?.error || 'Помилка завантаження даних сертифіката');
            } finally {
                setLoading(false);
            }
        };
        fetchCertificate();
    }, [id]);

    useEffect(() => {
        if (certificate && certificate.templateId?.hasQrCode !== false) {
            const verifyUrl = `${window.location.origin}/verify-certificate/${certificate.certificateId}`;
            QRCode.toDataURL(verifyUrl, { width: 120, margin: 1 })
                .then(url => setQrImg(url))
                .catch(() => setQrImg(null));
        }
    }, [certificate]);

    const loadImage = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = url;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
        });
    };

    const handleDownloadPDF = () => {
        if (!certificate) return;

        const logoUrl = `${window.location.origin}/km-logo.png`;
        const template = certificate.templateId || {};
        const bgType = template.backgroundType || 'classic';
        const verifyUrl = `${window.location.origin}/verify-certificate/${certificate.certificateId}`;

        // Generate QR code locally (no external API, no CORS issues)
        const generateQRDataUrl = async (url) => {
            if (template.hasQrCode === false) return null;
            try {
                return await QRCode.toDataURL(url, { width: 150, margin: 1 });
            } catch (e) { return null; }
        };

        const loadFont = async (url) => {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64data = reader.result.split(',')[1];
                    resolve(base64data);
                };
                reader.readAsDataURL(blob);
            });
        };

        const fontUrlRegular = '/fonts/Roboto-Regular.ttf';
        const fontUrlMedium = '/fonts/Roboto-Medium.ttf';
        const fontUrlItalic = '/fonts/Roboto-Italic.ttf';

        const trophyUrl = `${window.location.origin}/trophy.svg`;

        Promise.all([
            loadImage(logoUrl),
            loadImage(trophyUrl),
            generateQRDataUrl(verifyUrl),
            loadFont(fontUrlRegular),
            loadFont(fontUrlMedium),
            loadFont(fontUrlItalic)
        ]).then(([logoBase64, trophyBase64, qrBase64, regularFontBase64, mediumFontBase64, italicFontBase64]) => {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            doc.addFileToVFS('Roboto-Regular.ttf', regularFontBase64);
            doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');

            doc.addFileToVFS('Roboto-Medium.ttf', mediumFontBase64);
            doc.addFont('Roboto-Medium.ttf', 'Roboto', 'bold');

            doc.addFileToVFS('Roboto-Italic.ttf', italicFontBase64);
            doc.addFont('Roboto-Italic.ttf', 'Roboto', 'italic');

            doc.setFont('Roboto', 'normal');

            const studentFullName = `${certificate.studentName} ${certificate.studentLastName}`.trim();
            const dateStr = new Date(certificate.completedAt).toLocaleDateString('uk-UA');



            if (bgType === 'classic') {
                // Classic styling
                doc.setFillColor(253, 251, 247); // Light cream background
                doc.rect(0, 0, 297, 210, 'F');
                
                // Borders
                doc.setDrawColor(197, 160, 89); // Gold border (#c5a059)
                doc.setLineWidth(1.5);
                doc.rect(8, 8, 281, 194);
                doc.setLineWidth(0.6);
                doc.rect(10.5, 10.5, 276, 189);

                // Corner Ornaments
                const corners = [
                    { x: 10.5, y: 10.5, dx: 1, dy: 1 },
                    { x: 286.5, y: 10.5, dx: -1, dy: 1 },
                    { x: 10.5, y: 199.5, dx: 1, dy: -1 },
                    { x: 286.5, y: 199.5, dx: -1, dy: -1 }
                ];
                corners.forEach(c => {
                    doc.rect(c.x - (c.dx > 0 ? 0 : 5), c.y - (c.dy > 0 ? 0 : 5), 5, 5);
                    doc.line(c.x, c.y, c.x + c.dx * 8, c.y + c.dy * 8);
                });

                // Corporate Logo
                if (logoBase64) {
                    doc.addImage(logoBase64, 'PNG', 132.5, 15, 32, 16);
                }
                
                // Texts
                doc.setTextColor(138, 109, 37); // Gold title text (#8a6d25)
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(28);
                doc.text(template.titleText || 'СЕРТИФІКАТ', 148.5, 46, { align: 'center' });

                doc.setTextColor(100, 116, 139); // Gray subtext
                doc.setFont('Roboto', 'italic');
                doc.setFontSize(13);
                doc.text(template.subText || 'підтверджує, що', 148.5, 56, { align: 'center' });

                doc.setTextColor(15, 23, 42); // Navy name text
                doc.setFont('Roboto', 'italic');
                doc.setFontSize(30);
                doc.text(studentFullName, 148.5, 76, { align: 'center' });

                // Elegant Divider (Lines + Diamond)
                doc.setDrawColor(197, 160, 89);
                doc.setLineWidth(0.5);
                doc.line(90, 85, 135, 85);
                doc.line(162, 85, 207, 85);
                doc.setFillColor(197, 160, 89);
                doc.triangle(148.5, 83.5, 147, 85, 150, 85, 'F');
                doc.triangle(148.5, 86.5, 147, 85, 150, 85, 'F');

                doc.setTextColor(100, 116, 139);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(12);
                doc.text('про успішне проходження курсу:', 148.5, 96, { align: 'center' });

                doc.setTextColor(15, 23, 42);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(20);
                doc.text(certificate.testTitle, 148.5, 112, { align: 'center' });

                // Left footer: QR and Date
                if (qrBase64) {
                    doc.addImage(qrBase64, 'PNG', 35, 138, 26, 26);
                }
                doc.setTextColor(100, 116, 139);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(8);
                doc.text('ДАТА ВИДАЧІ', 48, 172, { align: 'center' });
                doc.setTextColor(15, 23, 42);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(10);
                doc.text(dateStr, 48, 178, { align: 'center' });

                // Center footer: Real Golden Trophy
                if (trophyBase64) {
                    doc.addImage(trophyBase64, 'PNG', 133.5, 140, 30, 33);
                }

                // Right footer: Signature
                doc.setTextColor(30, 58, 138); // Cursive script color
                doc.setFont('Roboto', 'italic');
                doc.setFontSize(18);
                doc.text(template.signatureName || 'Адміністрація', 248, 156, { align: 'center' });

                doc.setDrawColor(100, 116, 139);
                doc.setLineWidth(0.4);
                doc.line(213, 162, 283, 162);

                doc.setTextColor(100, 116, 139);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(8);
                doc.text((template.signatureText || 'Тренер').toUpperCase(), 248, 168, { align: 'center' });
                doc.setTextColor(15, 23, 42);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(10);
                doc.text(template.signatureName || 'Адміністрація', 248, 174, { align: 'center' });

            } else if (bgType === 'modern') {
                // Modern styling
                doc.setFillColor(15, 23, 42); // Dark slate background
                doc.rect(0, 0, 297, 210, 'F');

                // Left decorative bar
                doc.setFillColor(79, 70, 229); // Indigo accents
                doc.rect(0, 0, 15, 210, 'F');
                doc.setFillColor(16, 185, 129); // Emerald accent
                doc.rect(0, 180, 15, 30, 'F');

                // Ornaments
                doc.setDrawColor(79, 70, 229);
                doc.setLineWidth(1);
                doc.rect(25, 15, 257, 180);

                // Corporate Logo
                if (logoBase64) {
                    doc.addImage(logoBase64, 'PNG', 137, 22, 32, 16);
                }

                // Texts
                doc.setTextColor(255, 255, 255);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(32);
                doc.text(template.titleText || 'СЕРТИФІКАТ', 153, 52, { align: 'center' });

                doc.setTextColor(156, 163, 175);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(14);
                doc.text(template.subText || 'підтверджує, що', 153, 67, { align: 'center' });

                doc.setTextColor(16, 185, 129); // Emerald
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(26);
                doc.text(studentFullName, 153, 90, { align: 'center' });

                doc.setTextColor(156, 163, 175);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(14);
                doc.text('про успішне проходження курсу:', 153, 107, { align: 'center' });

                doc.setTextColor(255, 255, 255);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(22);
                doc.text(certificate.testTitle, 153, 126, { align: 'center' });

                // Signatures
                doc.setDrawColor(156, 163, 175);
                doc.line(45, 175, 115, 175);
                doc.setTextColor(156, 163, 175);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(11);
                doc.text(template.signatureText || 'Тренер', 80, 181, { align: 'center' });
                doc.setTextColor(255, 255, 255);
                doc.setFont('Roboto', 'bold');
                doc.text(template.signatureName || 'Адміністрація', 80, 187, { align: 'center' });

                // Date
                doc.setTextColor(156, 163, 175);
                doc.setFont('Roboto', 'normal');
                doc.text(`Дата: ${dateStr}`, 45, 160);

                if (qrBase64) {
                    doc.addImage(qrBase64, 'PNG', 230, 145, 30, 30);
                }

            } else {
                // Minimalist styling
                doc.setFillColor(255, 255, 255); // White background
                doc.rect(0, 0, 297, 210, 'F');

                // Borders
                doc.setDrawColor(229, 231, 235);
                doc.setLineWidth(0.5);
                doc.rect(15, 15, 267, 180);

                // Corporate Logo
                if (logoBase64) {
                    doc.addImage(logoBase64, 'PNG', 132.5, 22, 32, 16);
                }

                // Texts
                doc.setTextColor(17, 24, 39); // Charcoal
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(26);
                doc.text(template.titleText || 'СЕРТИФІКАТ', 148, 52, { align: 'center' });

                doc.setTextColor(107, 114, 128);
                doc.setFontSize(12);
                doc.text(template.subText || 'підтверджує, що', 148, 67, { align: 'center' });

                doc.setTextColor(17, 24, 39);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(24);
                doc.text(studentFullName, 148, 90, { align: 'center' });

                doc.setTextColor(107, 114, 128);
                doc.setFont('Roboto', 'normal');
                doc.setFontSize(12);
                doc.text('про успішне проходження курсу:', 148, 107, { align: 'center' });

                doc.setTextColor(17, 24, 39);
                doc.setFont('Roboto', 'bold');
                doc.setFontSize(20);
                doc.text(certificate.testTitle, 148, 126, { align: 'center' });

                // Signatures
                doc.setDrawColor(209, 213, 219);
                doc.line(40, 175, 110, 175);
                doc.setTextColor(107, 114, 128);
                doc.setFontSize(11);
                doc.text(template.signatureText || 'Тренер', 75, 181, { align: 'center' });
                doc.setTextColor(17, 24, 39);
                doc.setFont('Roboto', 'bold');
                doc.text(template.signatureName || 'Адміністрація', 75, 187, { align: 'center' });

                // Date
                doc.setTextColor(107, 114, 128);
                doc.setFont('Roboto', 'normal');
                doc.text(`Дата: ${dateStr}`, 40, 160);

                if (qrBase64) {
                    doc.addImage(qrBase64, 'PNG', 215, 145, 30, 30);
                }
            }

            doc.save(`certificate-${certificate.certificateId}.pdf`);
        }).catch((err) => {
            console.error('Failed to generate premium PDF:', err);
        });
    };

    if (loading) {
        return (
            <div className="cv-loading">
                <div className="spinner"></div>
                <p>Верифікація сертифіката...</p>
            </div>
        );
    }

    if (error || !certificate) {
        return (
            <div className="cv-error-container">
                <div className="cv-error-card">
                    <AlertTriangle size={48} className="error-icon" />
                    <h2>Помилка перевірки</h2>
                    <p>{error || 'Даний сертифікат не існує або був анульований.'}</p>
                    <Link to="/" className="btn-home">На головну</Link>
                </div>
            </div>
        );
    }

    const template = certificate.templateId || {};
    const dateStr = new Date(certificate.completedAt).toLocaleDateString('uk-UA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

    return (
        <div className="cv-page">
            <div className="cv-verify-badge">
                <ShieldCheck size={20} className="badge-icon" />
                <span>Сертифікат верифіковано та є дійсним в системі ServIQ</span>
            </div>

            {/* Certificate Render container */}
            <div className={`cv-certificate-border ${template.backgroundType || 'classic'}`}>
                {/* Corner decorations for classic */}
                {(template.backgroundType === 'classic' || !template.backgroundType) && (
                    <>
                        <div className="cert-corner top-left"></div>
                        <div className="cert-corner top-right"></div>
                        <div className="cert-corner bottom-left"></div>
                        <div className="cert-corner bottom-right"></div>
                    </>
                )}
                <div className="cv-certificate-content">
                    <img src="/km-logo.png" alt="Країна Мрій" className="cert-logo-img" />
                    
                    <h1 className="cert-title">{template.titleText || 'СЕРТИФІКАТ'}</h1>
                    <p className="cert-subtitle">{template.subText || 'про успішне проходження навчання'}</p>

                    <h2 className="cert-student-name">
                        {certificate.studentName} {certificate.studentLastName}
                    </h2>

                    <div className="cert-divider-container">
                        <div className="cert-divider-line"></div>
                        <div className="cert-divider-diamond"></div>
                        <div className="cert-divider-line"></div>
                    </div>

                    <p className="cert-course-label">про успішне проходження курсу:</p>
                    <h3 className="cert-course-title">{certificate.testTitle}</h3>

                    <div className="cert-footer">
                        <div className="cert-footer-left">
                            {template.hasQrCode !== false && qrImg && (
                                <div className="cert-qr-wrapper">
                                    <img src={qrImg} alt="QR Verification" className="cert-qr-img" />
                                    <span className="cert-qr-label">Код: {certificate.certificateId}</span>
                                </div>
                            )}
                            <p className="cert-date-label">Дата видачі</p>
                            <p className="cert-date-value">{dateStr}</p>
                        </div>

                        <div className="cert-footer-center">
                            {(template.backgroundType === 'classic' || !template.backgroundType) && (
                                <div className="cert-trophy-badge">
                                    <img src="/trophy.svg" alt="Кубок Переможця" className="cert-trophy-img" />
                                </div>
                            )}
                        </div>

                        <div className="cert-footer-right">
                            <div className="cert-signature-block">
                                <p className="cert-sig-handwritten">{template.signatureName || 'Адміністрація'}</p>
                                <div className="cert-signature-line"></div>
                                <p className="cert-sig-title">{template.signatureText || 'Тренер'}</p>
                                <p className="cert-sig-name">{template.signatureName || 'Адміністрація'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="cv-controls">
                <button className="cv-btn-download" onClick={handleDownloadPDF}>
                    <Download size={18} />
                    <span>Скачати сертифікат (PDF)</span>
                </button>
            </div>
        </div>
    );
};

export default CertificateVerify;
